/**
 * 設定ページ
 *
 * キャリブレーション状態、アプリ情報、テーマ設定を表示
 * 施設所属ユーザー（FACILITY_ADMIN, EXAMINER）のみアクセス可能
 */

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getCalibration, isCalibrated } from '../utils/calibration';

function SettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const calibration = getCalibration();
  const calibrated = isCalibrated();

  const roleLabel = user?.role === 'FACILITY_ADMIN' ? '施設管理者' : '検査者';

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">設定</h1>

      <div className="space-y-6">
        {/* ========================================
            キャリブレーション設定
            ======================================== */}
        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            キャリブレーション設定
          </h2>

          {calibrated ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">ステータス</span>
                  <p className="mt-1 font-medium text-green-600">設定済み</p>
                </div>
                <div>
                  <span className="text-gray-500">設定日時</span>
                  <p className="mt-1 font-medium text-gray-800">
                    {new Date(calibration.calibratedAt).toLocaleString('ja-JP')}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">画面解像度比率 (px/mm)</span>
                  <p className="mt-1 font-medium text-gray-800">
                    {calibration.ppmm.toFixed(2)}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">方式</span>
                  <p className="mt-1 font-medium text-gray-800">
                    {calibration.method === 'line' ? '線の実測' : 'カード合わせ'}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">画面サイズ</span>
                  <p className="mt-1 font-medium text-gray-800">
                    {calibration.screenWidthMm.toFixed(0)} x {calibration.screenHeightMm.toFixed(0)} mm
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-md p-3 mb-3">
              キャリブレーションが未実施です。正確な検査のために実施してください。
            </div>
          )}

          <div className="mt-4">
            <button
              onClick={() => navigate('/calibration')}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
            >
              {calibrated ? 'キャリブレーションを再実行' : 'キャリブレーションを実行'}
            </button>
          </div>
        </section>

        {/* ========================================
            アプリ情報
            ======================================== */}
        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            アプリ情報
          </h2>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">バージョン</span>
              <span className="font-medium text-gray-800">v0.1.0</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">ログインユーザー</span>
              <span className="font-medium text-gray-800">{user?.name}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">メールアドレス</span>
              <span className="font-medium text-gray-800">{user?.email}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">ロール</span>
              <span className="font-medium text-gray-800">{roleLabel}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-500">所属施設</span>
              <span className="font-medium text-gray-800">
                {user?.facility?.name || '未設定'}
              </span>
            </div>
          </div>
        </section>

        {/* ========================================
            テーマ設定（将来用プレースホルダー）
            ======================================== */}
        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            外観設定
          </h2>
          <p className="text-sm text-gray-500">
            テーマや表示設定は今後のアップデートで実装予定です。
          </p>
        </section>
      </div>
    </div>
  );
}

export default SettingsPage;
